import { useEffect, useRef, useState } from 'react'
import { usePhone, useRelaySend, useRelayCallbacks } from '../api'
import { useNotify } from '../components/Notifications'
import { useWebRTC } from '../api/webrtc'
import { TIMEOUTS } from '../config/constants'
import {
  halfFromStats, computeBitrateKbps, sendCallStats,
  type CallQualityHalf, type CallQualityAggregates,
} from '../lib/callStats'
import { computeQuality } from '../lib/callQuality'
import { aggregateTicks } from '../lib/tickBuffer'
import { isDtxTick } from '../lib/dtx'
import type { SignalLevel } from '../components/SignalBars'
import { useCallAudioBoost } from '../lib/useCallAudioBoost'
import { useCallBoost } from '../lib/useCallBoost'

const AGGREGATOR_VERSION = 1

/**
 * Errors from relay that should cancel "call pending" flag.
 * Keep in sync with relay/internal/signaling error reasons.
 */
const CALL_ERRORS = ['phone_busy', 'agent_offline', 'agent_timeout', 'already_in_call']

export interface UsePhoneCall {
  phone: ReturnType<typeof usePhone>['data']
  isLoading: boolean
  error: Error | null
  isMuted: boolean
  isCallPending: boolean
  isInCall: boolean
  /** 0..4 signal strength, undefined when not in-call. 0 = iceConnection failed/disconnected. */
  quality: SignalLevel | undefined
  webrtc: ReturnType<typeof useWebRTC>
  /**
   * Live audio boost state — operator preference, persisted in
   * localStorage. Unlike `isMuted` this does NOT reset on call_id
   * change; it carries across calls and across page refreshes.
   */
  boostEnabled: boolean
  boostGain: number
  handleCall: (number: string) => void
  handleHangup: () => void
  handleAnswer: () => void
  handleMute: () => void
  handleSetBoostEnabled: (v: boolean) => void
  handleSetBoostGain: (v: number) => void
}

/**
 * Owns the full dial/in-call state machine for a single phoneId.
 *
 * Four historical invariants that MUST survive any refactor of this hook
 * (each has a dedicated regression test in usePhoneCall.test.tsx):
 *
 *   1. isMuted resets to false when call_id transitions (otherwise mute
 *      "leaks" from one call to the next).
 *   2. isCallPending drops to false as soon as a currentCall appears or
 *      status becomes in-call (otherwise the UI stays stuck "dialling").
 *   3. WebRTC cleanup fires exactly once per call lifecycle. The dep is
 *      `webrtc.cleanup` (stable useCallback), NOT the `webrtc` object —
 *      the latter re-creates on every render and would kill the PC mid-ICE.
 *   4. Safety timeout TIMEOUTS.CALL_PENDING_MS clears a stuck pending flag
 *      if signalling never produced a dialing state.
 *
 * Also resets local state when phoneId changes (defence-in-depth; callers
 * should pass `key={phoneId}` to force unmount, this useEffect is the safety
 * net if they forget).
 */
export function usePhoneCall(phoneId: string): UsePhoneCall {
  const { data: phone, isLoading, error } = usePhone(phoneId)
  const [isMuted, setIsMuted] = useState(false)
  const [isCallPending, setIsCallPending] = useState(false)
  const send = useRelaySend()
  const notify = useNotify()
  const webrtc = useWebRTC(send, notify)

  useRelayCallbacks({
    onWebRTCOffer: webrtc.handleOffer,
    onIceCandidate: (_callId: string, candidate: string) => {
      webrtc.addIceCandidate(candidate)
    },
    onError: (reason: string) => {
      if (CALL_ERRORS.includes(reason)) {
        setIsCallPending(false)
      }
    },
  })

  // --- Live call audio boost ---
  // Boost state is operator-wide (not per-phone) and lives in a shared
  // hook so the sidebar settings panel and the in-call popover stay in
  // sync — flipping the switch in the sidebar must take effect on the
  // active call without hangup. Boost is intentionally NOT reset on
  // call_id transition (opposite of mute below).
  const {
    enabled: boostEnabled,
    gain: boostGain,
    setEnabled: handleSetBoostEnabled,
    setGain: handleSetBoostGain,
  } = useCallBoost()
  // Current remote MediaStream (replaced on each pc.ontrack fire).
  // useState — not useRef — so React re-renders trigger
  // useCallAudioBoost's effect deps when stream changes. setStream is
  // a stable setter, never enters cleanup deps; STABILITY INVARIANT
  // (lines below) preserved.
  const [stream, setStream] = useState<MediaStream | null>(null)

  // Bridge: webrtc notifies us when the remote stream arrives or goes
  // away. setOnRemoteStream is useCallback([], ...) (stable forever),
  // so this effect runs once. Pass `setStream` directly — also stable.
  const webrtcSetOnRemoteStream = webrtc.setOnRemoteStream
  useEffect(() => {
    webrtcSetOnRemoteStream(setStream)
    return () => webrtcSetOnRemoteStream(null)
  }, [webrtcSetOnRemoteStream])

  // Wire the boost graph. Hook is self-cleaning: on stream→null (fired
  // by webrtc.cleanup) the graph tears down; on unmount its own
  // useEffect return tears down. usePhoneCall does NOT call its
  // teardown explicitly anywhere.
  useCallAudioBoost(webrtc.audioElRef, stream, boostEnabled, boostGain)

  // #1, #2 — Reset mute and pending when call_id transitions
  const prevCallIdRef = useRef(phone?.currentCall?.callId)
  if (prevCallIdRef.current !== phone?.currentCall?.callId) {
    prevCallIdRef.current = phone?.currentCall?.callId
    setIsMuted(false)
    if (phone?.currentCall || phone?.status === 'in-call') {
      setIsCallPending(false)
    }
  }

  // --- Call quality snapshots ---
  // `startStats` is captured the first time we see a non-zero RTT after the
  // call reaches `active`. It's held locally and sent to the backend ONLY at
  // the end, bundled with the `end` half into one POST. This avoids two
  // separate round-trips and dodges the race with the reporter pipeline.
  const startStatsRef = useRef<CallQualityHalf | null>(null)
  const firstTickBytesRef = useRef<number | null>(null)
  const startTsRef = useRef<number | null>(null)
  const getLastStats = webrtc.getLastStats
  const getTickBuffer = webrtc.getTickBuffer

  useEffect(() => {
    if (phone?.currentCall?.state !== 'active') return
    if (startStatsRef.current !== null) return
    const snap = getLastStats()
    if (snap.rtt <= 0) return  // wait for a meaningful tick
    startStatsRef.current = halfFromStats(snap)
    firstTickBytesRef.current = snap.bytesReceived
    startTsRef.current = Date.now()
  }, [phone?.currentCall?.state, webrtc.stats, getLastStats])

  // #3 — Single-owner WebRTC cleanup + stats flush.
  // Fires when currentCallId → undefined OR the hook unmounts.
  // CRITICAL: dep is webrtc.cleanup (stable useCallback ref), NOT webrtc.
  // Ordering: (1) read last stats → (2) build combined payload → (3) POST
  // keepalive → (4) webrtc.cleanup() which closes the PC. Must happen in
  // that order — once the PC is closed, getStats() returns an empty report.
  const currentCallId = phone?.currentCall?.callId
  const prevCallIdForCleanup = useRef<string | undefined>(undefined)
  const webrtcCleanup = webrtc.cleanup
  const API_BASE = import.meta.env.VITE_API_URL as string

  // --- Stats flush ref (F-3 fix) ---
  // Holds the latest version of the stats-flush function. Updated in useEffect
  // (not during render) to satisfy react-hooks/refs lint rule. Called from
  // unmount/phoneId cleanup effects via ref — avoids adding unstable deps.
  // Double-flush protection: nulls prevCallIdForCleanup.current after send,
  // so a second call (normal hangup then unmount) is a no-op.
  const flushStatsRef = useRef<() => void>(() => {})
  useEffect(() => {
    flushStatsRef.current = () => {
      const callId = prevCallIdForCleanup.current
      if (!callId) return
      const endSnap = getLastStats()
      const endHalf: CallQualityHalf | null =
        endSnap.rtt > 0 || endSnap.packetsReceived > 0 || startStatsRef.current
          ? {
              ...halfFromStats(endSnap),
              bitrate_kbps: computeBitrateKbps({
                firstBytes: firstTickBytesRef.current,
                lastBytes: endSnap.bytesReceived,
                durationMs: startTsRef.current ? Date.now() - startTsRef.current : 0,
              }),
            }
          : null

      // v2 path — aggregate per-tick history into voice-only summary stats.
      // Returns null when there isn't enough voice data (short calls, pure
      // silence). Computed locally; never sends raw tick series.
      const agg = aggregateTicks(getTickBuffer())
      let aggregates: CallQualityAggregates | null = null
      if (agg !== null) {
        // legacy_quality_v1: what the OLD worst-of-3 formula would say,
        // saved alongside v2 as a regression sentinel. Computed against the
        // end snapshot the same way computeQualityFromRecord did for legacy.
        let legacyV1: SignalLevel = 1
        if (endHalf !== null && endHalf.packets_received >= 0) {
          const totalRcv = endHalf.packets_received + endHalf.packets_lost
          const lossRatio = totalRcv > 0 ? endHalf.packets_lost / totalRcv : 0
          legacyV1 = computeQuality({
            rtt: endHalf.rtt_ms,
            lossRatio,
            jitter: endHalf.jitter_ms,
          })
        }
        aggregates = {
          ...agg,
          legacy_quality_v1: legacyV1,
          aggregator_version: AGGREGATOR_VERSION,
        }
      }

      void sendCallStats(
        callId,
        { start: startStatsRef.current, end: endHalf, aggregates },
        API_BASE,
      )
      startStatsRef.current = null
      firstTickBytesRef.current = null
      startTsRef.current = null
      prevCallIdForCleanup.current = undefined
    }
  }) // no deps — updates after every render to capture latest refs

  // Flush + cleanup on call-id transition. Intentionally has no return
  // cleanup — the unmount effect below handles that. Splitting avoids the
  // "effect return fires on dep change, cleaning up the PC before we
  // snapshot end stats" ordering bug.
  useEffect(() => {
    const prev = prevCallIdForCleanup.current
    // Flush BEFORE updating ref — flushStatsRef reads prevCallIdForCleanup.current
    if (prev && !currentCallId) {
      flushStatsRef.current()
      webrtcCleanup()
    }
    prevCallIdForCleanup.current = currentCallId
  }, [currentCallId, webrtcCleanup])

  // Unmount-only cleanup. This effect has a stable dep, so its return fn
  // only fires once — when the hook unmounts (navigation, phoneId change
  // via caller `key` prop, etc.).
  // F-3 fix: flush stats BEFORE webrtcCleanup — once PC is closed,
  // getStats() returns an empty report. sendCallStats uses keepalive:true
  // so POST survives tab close.
  useEffect(() => {
    return () => {
      flushStatsRef.current()
      webrtcCleanup()
    }
  }, [webrtcCleanup])

  // #4 — Safety timeout for stuck pending state
  useEffect(() => {
    if (!isCallPending) return
    const timer = setTimeout(() => setIsCallPending(false), TIMEOUTS.CALL_PENDING_MS)
    return () => clearTimeout(timer)
  }, [isCallPending])

  // Defence-in-depth: tear down PC + reset local state when phoneId changes
  // on the same hook instance. Callers should still pass `key={phoneId}` to
  // force remount — this is the safety net for callsites that forget.
  //
  // webrtcCleanup is idempotent (see webrtc.ts cleanup), so double-invocation
  // with the unmount-only effect above on real remount is harmless.
  //
  // ⚠️ STABILITY INVARIANT — READ BEFORE TOUCHING THIS EFFECT OR ITS DEPS ⚠️
  //
  // `webrtcCleanup` MUST be a stable function reference for the lifetime of
  // the hook instance (same ref every render, not just the same behaviour).
  // React shallow-compares deps: if webrtcCleanup changes ref between renders,
  // this effect's *cleanup callback fires every render*, which means
  // `pc.close()` is called every render → audio drops mid-call in a tight
  // re-render loop. The bug is silent in the current tests because they
  // don't re-render without a phoneId change.
  //
  // The stability chain today (verify every step if you change any of them):
  //   1. Here:           const webrtcCleanup = webrtc.cleanup          (line ~110)
  //   2. webrtc.ts:50:   cleanup = useCallback(..., [stopStatsPolling])
  //   3. webrtc.ts:28:   stopStatsPolling = useCallback(..., [])
  //       └─ empty deps → stable forever → cleanup stable → webrtcCleanup stable.
  //
  // Breakers to watch for:
  //   - Removing `useCallback` from webrtc.cleanup or stopStatsPolling.
  //   - Adding unstable deps (send/notify/state) to cleanup's useCallback list
  //     — unstable because send/notify come from useRelaySend/useNotify and
  //     may change ref per render depending on their providers.
  //   - Wrapping webrtc.cleanup inside usePhoneCall (e.g. `const x = () =>
  //     webrtcCleanup()` without useCallback) and passing `x` to the dep.
  //
  // If you change webrtc.ts, add a test here that renders twice WITHOUT a
  // phoneId change and asserts `webrtcCleanupSpy` was NOT called between
  // renders. `.claude/rules/tdd.md` verify-via-revert applies.
  useEffect(() => {
    return () => {
      flushStatsRef.current()
      webrtcCleanup()
      setIsMuted(false)
      setIsCallPending(false)
      prevCallIdRef.current = undefined
      prevCallIdForCleanup.current = undefined
    }
  }, [phoneId, webrtcCleanup])

  const handleCall = (number: string) => {
    if (!phone) return
    setIsCallPending(true)
    send({ type: 'make_call', phone_id: phone.id, number })
  }

  const handleHangup = () => {
    if (phone?.currentCall) {
      send({ type: 'hangup', call_id: phone.currentCall.callId })
    }
    setIsMuted(false)
  }

  const handleAnswer = () => {
    if (phone?.currentCall) {
      send({ type: 'answer_call', call_id: phone.currentCall.callId })
    }
  }

  // Mute is browser-local: webrtc.setMuted disables the local mic track, so
  // the callee hears silence. The agent is deliberately NOT told — its only
  // outbound track carries the callee's voice, so a relayed mute there would
  // cut the wrong direction (operator would stop hearing the callee).
  const handleMute = () => {
    const newMuted = !isMuted
    setIsMuted(newMuted)
    webrtc.setMuted(newMuted)
  }

  const isInCall = Boolean(phone?.currentCall) || phone?.status === 'in-call'

  // Live DTX detection: read the last two entries from the tick buffer
  // (populated by webrtc.ts polling with monotonic ts) and check if the
  // current tick looks like Opus silence. If so, drop jitter from the live
  // quality so the badge doesn't flash Poor during a few seconds of dead-air.
  //
  // Reads-only: no Date.now() in render, no refs that mutate during render.
  let liveIsDtx = false
  if (isInCall && webrtc.stats.rtt > 0) {
    const buf = getTickBuffer()
    if (buf.length >= 2) {
      const cur = buf[buf.length - 1]
      const prev = buf[buf.length - 2]
      const dt = cur.ts - prev.ts
      if (dt > 0
          && cur.packetsReceived >= prev.packetsReceived
          && cur.bytesReceived >= prev.bytesReceived
          && cur.packetsLost >= prev.packetsLost) {
        const pps = (cur.packetsReceived - prev.packetsReceived) / (dt / 1000)
        const bitrateKbps = ((cur.bytesReceived - prev.bytesReceived) * 8) / dt
        const lossDelta = cur.packetsLost - prev.packetsLost
        liveIsDtx = isDtxTick({ pps, lossDelta, bitrateKbps })
      }
    }
  }

  // Derived quality bars. Undefined outside in-call (so PhoneStateCard hides
  // the bars). 0 when ICE has dropped. Otherwise compute from live stats.
  let quality: SignalLevel | undefined
  if (isInCall) {
    if (webrtc.connectionState === 'failed' || webrtc.connectionState === 'disconnected') {
      quality = 0
    } else if (webrtc.stats.rtt > 0) {
      const total = webrtc.stats.packetsLost + webrtc.stats.packetsReceived
      const lossRatio = total > 0 ? webrtc.stats.packetsLost / total : 0
      quality = computeQuality({
        rtt: webrtc.stats.rtt,
        lossRatio,
        jitter: liveIsDtx ? 0 : webrtc.stats.jitter,
      })
    } else {
      quality = 4  // optimistic before first stats tick
    }
  }

  return {
    phone,
    isLoading,
    error: (error as Error | null) ?? null,
    isMuted,
    isCallPending,
    isInCall,
    quality,
    webrtc,
    boostEnabled,
    boostGain,
    handleCall,
    handleHangup,
    handleAnswer,
    handleMute,
    handleSetBoostEnabled,
    handleSetBoostGain,
  }
}
